import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MessageDescriptor } from '@lingui/core';
import { defineMessage } from '@lingui/core/macro';
import { cn } from '@shared/lib/utils';
import { localizePath, stripLocaleFromPathname, useLocale } from '@shared/i18n';
import { Badge } from '@shared/ui/badge';
import { useIntentPrefetch } from '@shared/hooks/useIntentPrefetch';
import { useNavPrefetch } from './useNavPrefetch';
import {
    Home,
    FileText,
    Plane,
    CreditCard,
    MessageCircle,
    Calendar,
    Users
} from 'lucide-react';
import type { NavItem } from '@shared/lib/navigation';
import type { UserRole } from '@shared/types/auth';

export interface MobileBottomNavProps {
    navItems: NavItem[];
    navKeys: readonly string[];
    role: UserRole;
    shortLabels?: Record<string, string>;
    unreadCount?: number;
    disableNavPrefetch?: boolean;
}

// Icon mapping for all possible mobile nav items
const NAV_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
    dashboard: Home, // Default, can be overridden if item has icon
    quotes: FileText,
    travel: Plane,
    payments: CreditCard,
    messages: MessageCircle,
    patients: Users,
    calendar: Calendar, // If used
};

// Default short labels
const DEFAULT_SHORT_LABELS: Record<string, MessageDescriptor> = {
    dashboard: defineMessage({
        id: 'shared.layout.MobileBottomNav.home',
        message: 'Home',
    }),
    quotes: defineMessage({
        id: 'shared.layout.MobileBottomNav.quotes',
        message: 'Quotes',
    }),
    travel: defineMessage({
        id: 'shared.layout.MobileBottomNav.travel',
        message: 'Travel',
    }),
    payments: defineMessage({
        id: 'shared.layout.MobileBottomNav.pay',
        message: 'Pay',
    }),
    messages: defineMessage({
        id: 'shared.layout.MobileBottomNav.chat',
        message: 'Chat',
    }),
    patients: defineMessage({
        id: 'shared.layout.MobileBottomNav.patients',
        message: 'Patients',
    }),
    calendar: defineMessage({
        id: 'shared.layout.MobileBottomNav.calendar',
        message: 'Calendar',
    }),
};

/**
 * Shared floating pill-shaped bottom navigation for mobile experience
 * 
 * Features:
 * - Glassmorphism effect with backdrop blur
 * - Active state with gradient pill indicator
 * - Unread message badge with animation
 * - iOS safe area support
 * - Accessible with ARIA labels and keyboard navigation
 * - Optimized touch targets (minimum 44px)
 */
export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
    navItems,
    navKeys,
    role,
    shortLabels = {},
    unreadCount = 0,
    disableNavPrefetch = false,
}) => {
    const location = useLocation();
    const { i18n } = useLingui();
    const { locale } = useLocale();
    const { getIntentHandlers } = useIntentPrefetch();
    const { prefetchPath } = useNavPrefetch();
    const normalizedPathname = React.useMemo(
        () => stripLocaleFromPathname(location.pathname),
        [location.pathname]
    );

    // Memoize filtered nav items to prevent unnecessary re-renders
    const bottomNavItems = React.useMemo(() => {
        return navKeys
            .map(key => navItems.find(item => item.key === key))
            .filter((item): item is NavItem => item !== undefined);
    }, [navItems, navKeys]);

    // Helper to get translated label
    const getLabel = React.useCallback((item: NavItem): string => {
        if (typeof item.label === 'string') {
            return item.label;
        }
        return i18n._(item.label as MessageDescriptor);
    }, [i18n]);

    // Check if a path is active (exact or nested)
    const isActivePath = React.useCallback((itemPath: string): boolean => {
        // Exact match
        if (normalizedPathname === itemPath) return true;
        // Nested match (e.g., /quotes/123)
        if (normalizedPathname.startsWith(itemPath + '/')) return true;
        // Handle dashboard as home
        if (itemPath === '/dashboard' && normalizedPathname === '/') return true;
        return false;
    }, [normalizedPathname]);

    const mergedShortLabels = React.useMemo<Record<string, string>>(
        () => ({
            ...Object.fromEntries(
                Object.entries(DEFAULT_SHORT_LABELS).map(([key, descriptor]) => [key, i18n._(descriptor)])
            ),
            ...shortLabels,
        }),
        [i18n, shortLabels]
    );

    // Early return if no nav items (edge case)
    if (bottomNavItems.length === 0) {
        return null;
    }

    return (
        <nav
            className={cn(
                "fixed bottom-0 left-0 right-0 z-50",
                "pointer-events-none",
                "md:hidden", // Hide on tablet and desktop
                "mobile-bottom-nav"
            )}
            role="navigation"
            aria-label={t({
                id: 'shared.layout.MobileBottomNav.main-navigation',

                message: 'Main navigation',
            })}
        >
            {/* Safe area + padding container */}
            <div className="px-4 pb-4 pb-safe">
                {/* Clickable area container */}
                <div className="pointer-events-auto mx-auto max-w-sm">
                    {/* Pill container with frosted glass effect */}
                    <div
                        className={cn(
                            "flex items-center justify-around gap-1",
                            "h-[68px] rounded-[22px]",
                            // Frosted glass effect
                            "bg-white/80 dark:bg-neutral-900/85",
                            "backdrop-blur-2xl backdrop-saturate-200",
                            "border border-white/40 dark:border-white/10",
                            "shadow-2xl floating-nav",
                            "px-2"
                        )}
                    >
                        {bottomNavItems.map((item) => {
                            const isActive = isActivePath(item.path);
                            // Use mapped icon or item's own icon
                            const Icon = NAV_ICONS[item.key] || item.icon;
                            // Need to handle different dashboard icons for doctor vs patient if needed, 
                            // but usually item.icon comes from navigation.ts which is correct per role.

                            const isMessages = item.key === 'messages';
                            const resolvedBadgeCount = item.badge ?? (isMessages ? unreadCount : 0);
                            const showBadge = resolvedBadgeCount > 0;
                            const shortLabel = mergedShortLabels[item.key] || getLabel(item);
                            const fullLabel = getLabel(item);
                            const localizedItemPath = localizePath(item.path, locale);
                            const intentHandlers = disableNavPrefetch
                                ? undefined
                                : getIntentHandlers(item.path, () => {
                                    void prefetchPath({
                                        path: item.path,
                                        role,
                                    });
                                });

                            return (
                                <Link
                                    key={item.key}
                                    to={localizedItemPath}
                                    className={cn(
                                        // Base layout
                                        "relative flex flex-col items-center justify-center",
                                        "flex-1", // Equal width distribution
                                        "min-w-0", // Allow shrinking
                                        "h-14", // Touch target height
                                        "rounded-xl",
                                        // Transitions
                                        "transition-all duration-200 ease-out",
                                        // Focus states for accessibility
                                        "focus-visible:outline-none",
                                        "focus-visible:ring-2 focus-visible:ring-brand-blue",
                                        "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                                        // Active vs inactive states
                                        isActive
                                            ? "nav-item-active text-white scale-105 shadow-brand-blue/30 shadow-lg"
                                            : "text-muted-foreground hover:text-foreground active:scale-95 transition-transform"
                                    )}
                                    aria-label={fullLabel}
                                    aria-current={isActive ? 'page' : undefined}
                                    {...intentHandlers}
                                >
                                    {/* Icon container with badge */}
                                    <div className="relative flex items-center justify-center">
                                        <Icon
                                            className={cn(
                                                "h-5 w-5",
                                                "transition-transform duration-200",
                                                isActive && "scale-110"
                                            )}
                                            aria-hidden="true"
                                        />

                                        {/* Unread badge for messages */}
                                        {showBadge && (
                                            <Badge
                                                variant="destructive"
                                                className={cn(
                                                    "absolute -top-2 -right-3",
                                                    "h-4 min-w-4 px-1",
                                                    "text-[10px] font-bold leading-none",
                                                    "flex items-center justify-center",
                                                    "rounded-full",
                                                    "border-2 border-background",
                                                    "shadow-sm"
                                                )}
                                                aria-label={t({
                                                    id: 'shared.layout.MobileBottomNav.resolvedbadgecount-unread-items',

                                                    message: '{resolvedBadgeCount} unread items',
                                                }, {
                                                    resolvedBadgeCount: resolvedBadgeCount,
                                                })}
                                            >
                                                {resolvedBadgeCount > 99 ? '99+' : resolvedBadgeCount}
                                            </Badge>
                                        )}
                                    </div>
                                    {/* Label - always visible for better UX */}
                                    <span
                                        className={cn(
                                            "text-[11px] font-medium leading-none",
                                            "mt-1",
                                            "truncate max-w-full px-1",
                                            isActive ? "text-white" : "text-inherit"
                                        )}
                                    >
                                        {shortLabel}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>
        </nav>
    );
};
